<script lang="ts" module>
  import { checkLegacyPermissionsAPI, requestCameraPermission } from "@/libs/camera"
  import { readable } from "svelte/store"

  const isLegacyPermissionsAPI = readable(false, (set) => {
    checkLegacyPermissionsAPI().then(set)
  })
</script>

<script lang="ts">
  import { ENTRY_POINT, SOURCE } from "@/config"
  import Tutorial from "@/features/tutorial/Tutorial.svelte"
  import { t } from "@/libs/i18n"
  import { Button, KIND, SIZE } from "@/libs/ui/Button"
  import { FileUploader } from "@/libs/ui/FileUploader"
  import { ICON, Icon } from "@/libs/ui/Icon"
  import Message from "@/libs/ui/Message/Message.svelte"
  import ShapedContainer, { SHAPE } from "@/libs/ui/ShapedContainer/ShapedContainer.svelte"
  import { generateKey } from "@/libs/ui/svelte/key.svelte"
  import { getApplicationStore } from "@/stores/application"
  import { getCameraPermissionStore } from "@/stores/camera-permission"
  import { onMount } from "svelte"
  import TutorialBanner from "../tutorial/TutorialBanner.svelte"
  import CompanionInvite from "./companion/CompanionInvite.svelte"
  import { analytics } from "@/libs/telemetry"

  let {
    onCamera,
    onFiles,
    onUpload,
    isRetaking = false,
  }: {
    onCamera(): void
    onUpload(file: File): void
    onFiles(files: File[]): void
    isRetaking?: boolean
  } = $props()

  const cameraPermissionStore = getCameraPermissionStore()
  const applicationStore = getApplicationStore()

  // Manually trigger camera permission request to update state for Safari users, see camera-permission.ts
  const updateCameraPermissionState = async () => {
    isRequestingCameraPermission = true
    try {
      const granted = await requestCameraPermission()
      cameraPermissionStore.refresh({ expectedState: granted ? "granted" : "denied" })
      return granted
    } catch (error) {
      cameraPermissionStore.refresh({ expectedState: "denied" })
      return false
    } finally {
      isRequestingCameraPermission = false
    }
  }

  onMount(() => updateCameraPermissionState())

  let isRequestingCameraPermission = $state(false)

  const handleCameraSelect = async () => {
    const granted = await updateCameraPermissionState()
    if (granted) onCamera()
  }

  const handleUpload = (file: File) => {
    onUpload(file)
  }

  const handleUploadClick = () => {
    const isCompanionMode = new URLSearchParams(location.search).get("session") !== null
    const eventContent = isCompanionMode ? "companion" : "start_screen"
    analytics.photo_upload({ event_content: eventContent })
  }

  let isCompanionFlowStarted = $state(false)

  const handleCompanionStart = () => (isCompanionFlowStarted = true)
  const handleCompanionAbort = () => (isCompanionFlowStarted = false)

  let isTutorialEntryPoint = $derived(
    $applicationStore.config.entryPoint === ENTRY_POINT.TUTORIAL && !isRetaking,
  )
  let isTutorialOpen = $state(
    $applicationStore.config.entryPoint === ENTRY_POINT.TUTORIAL && !isRetaking,
  )

  const handleTutorialOpen = () => {
    analytics.instructions_opened()
    isTutorialOpen = true
  }
  const handleTutorialClose = () => {
    isTutorialOpen = false
  }

  let hasCompanionOption = $derived($applicationStore.config.sources.includes(SOURCE.COMPANION))
  let hasCameraOption = $derived(
    Boolean(
      $applicationStore.config.sources.find(
        (source) => source === SOURCE.FRONT_CAMERA || source === SOURCE.BACK_CAMERA,
      ),
    ),
  )
  let hasUploadOption = $derived($applicationStore.config.sources.includes(SOURCE.UPLOAD))
  let hasTutorial = $derived(Boolean($applicationStore.config.tutorial))

  let isPermissionDenied = $derived(hasCameraOption && $cameraPermissionStore === "denied")

  let cameraOptionTitle = $derived(
    $t.source_selection[hasCompanionOption ? "continue_on_desktop" : "camera_video"],
  )

  let title = $derived.by(() => {
    if (hasCompanionOption) return ""
    if (isPermissionDenied) return $t.source_selection.camera_permissions_warning.title
    return $t.source_selection.title
  })
  let description = $derived.by(() => {
    if (hasCompanionOption || !isPermissionDenied) return undefined

    return $t.source_selection.camera_permissions_warning[
      $isLegacyPermissionsAPI ? "instructions_reload" : "instructions_reset"
    ]
  })
  let icons = $derived.by(() => {
    if (hasCompanionOption) return []
    if (isPermissionDenied) return [{ type: ICON.CAMERA_SLASH, class: "icon-camera-access-denied" }]

    return hasTutorial
      ? []
      : [
          { type: ICON.CAMERA, class: "icon-camera-lg" },
          { type: ICON.PICTURE_2, class: "icon-upload-lg" },
        ]
  })

  let key = generateKey(() => title)
</script>

{#if hasTutorial && !isTutorialOpen && hasCameraOption && hasUploadOption && !hasCompanionOption}
  <div class="absolute z-20 w-full bg-transparent p-4">
    <TutorialBanner onclick={handleTutorialOpen} />
  </div>
{:else if isTutorialOpen}
  <Tutorial
    isEntryPoint={isTutorialEntryPoint}
    onClose={handleTutorialClose}
    onTakePhoto={() => {
      handleTutorialClose()
      handleCameraSelect()
    }}
    {onUpload}
  />
{/if}

{#if hasCompanionOption}
  <CompanionInvite
    onStart={handleCompanionStart}
    onAbort={handleCompanionAbort}
    onDone={onFiles}
    footer={buttons}
  />
{:else}
  <Message
    class={[
      "source-selection", // anchor for external customization
    ]}
    {key}
    {title}
    {description}
    footer={buttons}
  >
    {#if icons.length > 0}
      <div class="contents">
        <ShapedContainer
          shape={SHAPE.RECTANGLE}
          class={[
            "flex size-16 overflow-hidden",
            isPermissionDenied ? "bg-[#EB5545]" : "icons-container",
          ]}
        >
          {#each icons as icon}
            <Icon {...icon} size={48} class={[icon.class, "m-auto shrink-0 basis-full"]} />
          {/each}
        </ShapedContainer>
      </div>
    {/if}
  </Message>
{/if}

{#snippet buttons()}
  {#if !isCompanionFlowStarted}
    {#if hasCameraOption}
      <Button
        class="w-full liqa-lg:max-w-[16rem]"
        type="button"
        onclick={handleCameraSelect}
        disabled={isRequestingCameraPermission || isPermissionDenied}
        kind={hasCompanionOption ? KIND.SECONDARY : KIND.PRIMARY}
        size={SIZE.MEDIUM}
        aria-label={cameraOptionTitle}
      >
        <Icon type={ICON.CAMERA} class="shrink-0" />
        {cameraOptionTitle}
      </Button>
    {/if}
    {#if hasUploadOption}
      <FileUploader
        class="w-full liqa-lg:max-w-[16rem]"
        disabled={isRequestingCameraPermission}
        size={SIZE.MEDIUM}
        kind={KIND.SECONDARY}
        aria-label={$t.source_selection.upload_photo}
        onUpload={handleUpload}
        onClick={handleUploadClick}
      >
        <Icon type={ICON.PICTURE} class="shrink-0" />
        {$t.source_selection.upload_photo}
      </FileUploader>
    {/if}
  {/if}
{/snippet}

<style lang="postcss">
  :where(*) {
    :global {
      :where(.icon-camera-lg, .icon-upload-lg, .icons-container) {
        animation-duration: 6.5s;
        animation-timing-function: linear;
        animation-iteration-count: infinite;
      }

      :where(.icons-container) {
        animation-name: gradient;
      }

      :where(.icon-camera-lg, .icon-upload-lg) {
        animation-delay: 600ms;
      }
      :where(.icon-camera-lg) {
        animation-name: slide-1;
      }
      :where(.icon-upload-lg) {
        animation-name: slide-2;
      }
    }

    /**
    * Total time - 6500ms
    *
    * Gradient
    *  - green - 3000ms
    *  - green -> semi-transparent white  - 600ms
    *    - green -> transparent white - 350ms
    *    - transparent white -> semi-transparent white - 250ms
    *  - semi-transparent white - 2650ms
    *  - semi-transparent white -> green - 250ms
    */
    @keyframes gradient {
      0%, /* 0/6000 */
    46.15% /* 3000/6500 */ {
        background-color: #50d585;
      }
      52.53% /* 3350/6500 */ {
        background-color: rgba(var(--color) / 0.1);
      }
      55.38%, /* 3600/6500 */
    96.15% /* 6250/6500 */ {
        background-color: rgba(var(--color) / 0.2);
      }
      100% /* 6000/6000 */ {
        background-color: #50d585;
      }
    }

    /**
    * Total time - 6500ms
    *
    * Slide
    *  - delay 600ms
    *  - camera - 2400ms
    *  - camera -> gallery - 250ms
    *
    *  - gallery bounce - 600ms
    *  - gallery - 2400ms
    *  - gallery -> camera - 250ms
    *
    *  - camera bounce - 600ms
    *  - camera - 2400ms
    */
    @keyframes slide-1 {
      0%, /* 0/6000 */
    36.92%  /* 2400/6500 */ {
        transform: translateX(0);
        opacity: 1;
      }

      /* Slide forward */
      40.76% /* 2650/6500 */ {
        transform: translateX(-100%);
        opacity: 1;
      }

      /** Bounce */
      42.30% /* 2750/6500 */ {
        transform: translateX(-114%);
        opacity: 1;
      }
      42.301% /* next tick */ {
        transform: translateX(-114%);
        opacity: 0;
      }
      46.15% /* 3000/6500 */ {
        transform: translateX(-93%);
        opacity: 0;
      }
      50%, /* 3250/6500 */
    86.92% /* 5650/6500 */ {
        transform: translateX(-100%);
        opacity: 0;
      }
      86.921% /* next tick */ {
        transform: translateX(-100%);
        opacity: 1;
      }

      /* Slide backward */
      90.76% /* 5900/6500 */ {
        transform: translateX(0%);
        opacity: 1;
      }
      /** Bounce */
      92.30% /* 6000/6500 */ {
        transform: translateX(14%);
        opacity: 1;
      }
      96.15% /* 6250/6500 */ {
        transform: translateX(-7%);
        opacity: 1;
      }
      100% /* 6000/6500 */ {
        transform: translateX(0%);
        opacity: 1;
      }
    }
    /**
    * Total time - 6500ms
    *
    * Slide
    *  - delay 600ms
    *  - camera - 2400ms
    *  - camera -> gallery - 250ms
    *
    *  - gallery bounce - 600ms
    *  - gallery - 2400ms
    *  - gallery -> camera - 250ms
    *
    *  - camera bounce - 600ms
    *  - camera - 2400ms
    */
    @keyframes slide-2 {
      0%, /* 0/6000 */
    36.92%  /* 2400/6500 */ {
        transform: translateX(0);
        opacity: 0;
      }
      36.921%  /* next tick */ {
        transform: translateX(0);
        opacity: 1;
      }

      /* Slide forward */
      40.76% /* 2650/6500 */ {
        transform: translateX(-100%);
        opacity: 1;
      }

      /** Bounce */
      42.30% /* 2750/6500 */ {
        transform: translateX(-114%);
        opacity: 1;
      }
      46.15% /* 3000/6500 */ {
        transform: translateX(-93%);
        opacity: 1;
      }
      50%, /* 3250/6500 */
    86.92% /* 5650/6500 */ {
        transform: translateX(-100%);
        opacity: 1;
      }

      /* Slide backward */
      90.76% /* 5900/6500 */ {
        transform: translateX(0%);
        opacity: 1;
      }

      /** Bounce */
      92.30% /* 6000/6500 */ {
        transform: translateX(14%);
        opacity: 1;
      }
      92.301% /* next tick */ {
        transform: translateX(14%);
        opacity: 0;
      }
      96.15% /* 6250/6500 */ {
        transform: translateX(-7%);
        opacity: 0;
      }
      100% /* 6000/6500 */ {
        transform: translateX(0%);
        opacity: 0;
      }
    }
  }
</style>
